import { Form } from 'antd';
import { BaseButton } from 'components/UI/BaseButton';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectGlobalUserInfo } from 'state/global/auth/selector';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { hasUserInGroup, USER_GROUP_NAME } from 'util/user-group';
import { addNewMachine } from '../../state/operation';
import * as selector from '../../state/selector';
import { fieldKey } from '../../util/constant';
import { CustomInputMask } from './components/CustomInputMask';
import { InputValidation } from './components/InputValidation';

export const ClaimingProcessForm = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.CLAIMING_PROCESS);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [, onFormValuesChange] = useState({});
  const { claimingProcessInfo } = useSelector((state) => ({
    claimingProcessInfo: selector.selectClaimingProcess(state)
  }));

  const globalUserInfo = useSelector((state) => selectGlobalUserInfo(state));
  const isDeveloperUser = useMemo(
    () => hasUserInGroup(globalUserInfo, USER_GROUP_NAME.DEVELOPER),
    [globalUserInfo]
  );

  const onFinish = useCallback(
    (values) => {
      const newMachine = {
        materialNumber: values.materialNumber,
        serialNumber: values.serialNumber,
        claimingCode: values.claimingCode.replace(/-/g, '')
      };
      dispatch(addNewMachine(newMachine));
    },
    [dispatch]
  );

  const onValuesChange = useCallback(() => {
    onFormValuesChange({});
  }, []);

  const formItemLayout = {
    wrapperCol: {
      span: 16
    }
  };

  return (
    <div className="claimingProcess-form">
      <Form form={form} onValuesChange={onValuesChange} onFinish={onFinish}>
        <div className="claimingProcess-form-item">
          <Form.Item
            {...formItemLayout}
            name={fieldKey.materialNumber}
            rules={[
              {
                required: true,
                message: t('form.errors.materialNumberRequired')
              },
              {
                required: true,
                pattern: new RegExp(/^[1]\.\d{3}[-]\d{3}\.\d{1}$/g),
                message: t('form.errors.materialNumberFormat')
              }
            ]}>
            <CustomInputMask
              mask="1.999-999.9"
              placeholder="_.___-___._"
              label={t('form.fields.materialNumber')}
            />
          </Form.Item>
        </div>

        <div className="claimingProcess-form-item">
          {!isDeveloperUser ? (
            <Form.Item
              name={fieldKey.serialNumber}
              {...formItemLayout}
              rules={[
                {
                  required: true,
                  message: t('form.errors.serialNumberRequired')
                },
                {
                  required: true,
                  pattern: new RegExp(/^\d{6}$/g),
                  message: t('form.errors.serialNumberFormat')
                }
              ]}>
              <CustomInputMask
                mask="999999"
                placeholder="______"
                label={t('form.fields.serialNumber')}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name={fieldKey.serialNumber}
              {...formItemLayout}
              rules={[
                {
                  required: true,
                  message: t('form.errors.serialNumberRequired')
                }
              ]}>
              <InputValidation label={t('form.fields.serialNumber')} />
            </Form.Item>
          )}
        </div>

        <div className="claimingProcess-form-item">
          <Form.Item
            name={fieldKey.claimingCode}
            {...formItemLayout}
            rules={[
              {
                required: true,
                message: t('form.errors.claimingCodeRequired')
              },
              {
                required: true,
                pattern: new RegExp(/^([a-zA-Z0-9]{5}[-]){4}[a-zA-Z0-9]{2}/g),
                message: t('form.errors.claimingCodeFormat')
              }
            ]}>
            <CustomInputMask
              mask="*****-*****-*****-*****-**"
              placeholder="_____-_____-_____-_____-__"
              label={t('form.fields.claimingCode')}
            />
          </Form.Item>
        </div>

        <Form.Item>
          <BaseButton
            className="base-yellow-btn addition-machine-btn"
            htmlType="submit"
            loading={claimingProcessInfo.isLoading}>
            {t('form.submitButton')}
          </BaseButton>
        </Form.Item>
      </Form>
    </div>
  );
};
